/**
 * Analytics Guide Modal Component
 * Explains how metrics are calculated and where they come from
 */
import React from 'react';
import { createPortal } from 'react-dom';

export default function GuideModal({ onClose }) {
  // Use React portal to mount modal outside header flow (prevents clipping)
  const modalContent = (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Analytics Guide</h2>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>
        <div style={styles.content}>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Period Revenue</h3>
            <p style={styles.description}>
              Total revenue from all confirmed reservations with check-in dates within the selected reporting period. 
              Excludes cancelled reservations. Revenue is calculated from the reservation's total price.
            </p>
            <p style={styles.source}>Source: fact_hostaway_reservation (total_price field)</p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Reservations</h3>
            <p style={styles.description}>
              Count of confirmed reservations with check-in dates within the selected reporting period. 
              Each reservation is counted once, even if it spans multiple months.
            </p>
            <p style={styles.source}>Source: fact_hostaway_reservation (reservation_id, deduplicated)</p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Average Daily Rate (ADR)</h3>
            <p style={styles.description}>
              Average revenue per night, calculated as total revenue divided by total nights booked. 
              ADR helps assess pricing effectiveness over time.
            </p>
            <p style={styles.source}>Source: Calculated from revenue and nights</p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Occupancy Rate</h3>
            <p style={styles.description}>
              Occupancy is nights booked (including owner stays) divided by days in the month. 
              Same calculation as the Calendar KPI. Always 0–100%. 100% means every night in the month was booked.
            </p>
            <p style={styles.source}>Source: Nights booked (including owner stays) and calendar days</p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Lead Time</h3>
            <p style={styles.description}>
              This chart shows how far in advance guests booked their stay. For example, &quot;15–30 days&quot; means the reservation was made between 15 and 30 days before check-in. Bookings made closer to check-in tend to be less predictable.
            </p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Length of Stay</h3>
            <p style={styles.description}>
              Number of nights for each reservation, calculated from check-in to check-out dates 
              (end-exclusive). Helps understand booking patterns and guest preferences.
            </p>
            <p style={styles.source}>Source: checkout_date - checkin_date (nights)</p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Master Channels</h3>
            <p style={styles.description}>
              Booking sources are grouped into four master channels:
            </p>
            <ul style={styles.list}>
              <li><strong>VRBO</strong> (#4A6FAE): vrboical, homeaway</li>
              <li><strong>Airbnb</strong> (#F2BFC0): airbnbofficial</li>
              <li><strong>CLC</strong> (#C7E0F4): bookingengine, direct, clcvacationhomes, CLC-Lease</li>
              <li><strong>Owner</strong> (#D8CFC4): mondayownerres, ownerstay</li>
            </ul>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Reporting Period</h3>
            <p style={styles.description}>
              All metrics are calculated for the selected reporting period only. The period is 
              end-exclusive internally (e.g., Jan 1 to Feb 1 includes Jan 1-31) but displayed 
              as inclusive dates in the UI.
            </p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Property Scoping</h3>
            <p style={styles.description}>
              All analytics are scoped to the currently selected property. Metrics never aggregate 
              across your portfolio unless you explicitly select a different property.
            </p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Future Data</h3>
            <p style={styles.description}>
              Future months are based on current bookings and may fluctuate with new bookings/cancellations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Portal to document.body to avoid header clipping
  return typeof document !== 'undefined' 
    ? createPortal(modalContent, document.body)
    : modalContent;
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000, // Above sticky header (HEADER_Z_INDEX = 150) and all content
    padding: '20px',
    overflow: 'auto',
    // Ensure modal is never clipped by parent containers
    isolation: 'isolate',
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: 'calc(100vh - 40px)',
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '10px',
    minWidth: '44px',
    minHeight: '44px',
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  content: {
    padding: '24px',
    overflowY: 'auto',
    flex: '1 1 auto',
    // Ensure scrollbar is visible (WebKit and Firefox)
    scrollbarWidth: 'thin',
    scrollbarColor: '#cbd5e1 #f1f5f9',
    // WebKit scrollbar styling
    WebkitOverflowScrolling: 'touch',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '8px',
  },
  description: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: '1.6',
    marginBottom: '8px',
  },
  source: {
    fontSize: '12px',
    color: '#6b7280',
    fontStyle: 'italic',
  },
  list: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: '1.8',
    paddingLeft: '20px',
  },
};
